import { useState } from "react"

import { useForm } from "@tanstack/react-form"

import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Separator } from "@/components/ui/Separator"

import { LoginPageController } from "../LoginPage.controller"

export function ClasscodeLogin({
  controller,
}: {
  controller: LoginPageController
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [hideStudentWork, setHideStudentWork] = useState(false)
  const form = useForm({
    defaultValues: {
      classCode: "",
    },
  })

  return (
    <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-w-[350px] tw-max-w-full tw-flex tw-flex-col">
      <div className="tw-h-[10em] tw-flex tw-items-center tw-justify-center">
        <h1 className="tw-text-4xl tw-text-gray-700">Start Working</h1>
      </div>

      <div className="tw-px-12 tw-pb-1 tw-flex tw-flex-col tw-gap-4">
        <form.Field
          name="classCode"
          validators={{
            onChangeAsyncDebounceMs: 500,
            onChangeAsync: async ({ value }: { value: string }) => {
              setIsLoading(true)
              const result = await controller.checkClassCodeValidity(value)
              setIsLoading(false)
              setHideStudentWork(result.hideStudentWork || false)
              return result.errorMessage
            },
          }}
        >
          {(field) => (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <label
                htmlFor={field.name}
                className="tw-text-2xl tw-font-medium tw-text-gray-600"
              >
                Class Code
              </label>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) =>
                  field.handleChange(e.target.value.toUpperCase())
                }
                maxLength={6}
                className="tw-text-3xl tw-border-2 tw-rounded-md tw-border-gray-500 tw-px-3 tw-py-2 focus:tw-border-emerald-600 focus:tw-outline-none"
                autoCapitalize="characters"
                disabled={controller.isLoading}
              />
              {hideStudentWork && (
                <p className="tw-text-center tw-text-sm tw-text-red-600">
                  This assignment is hidden. Please ask your teacher to un-hide
                  it.
                </p>
              )}
              {field.state.meta.errors.length > 0 ? (
                <p className="tw-text-medium tw-text-red-600 tw-mt-1">
                  {field.state.meta.errors[0]}
                </p>
              ) : null}
            </div>
          )}
        </form.Field>

        <div className="tw-flex tw-items-center tw-gap-2 tw-my-2">
          <Separator className="tw-flex-1 tw-h-0.5 tw-bg-gray-600" />
          <span className="tw-text-gray-600 tw-text-xl tw-font-medium tw-leading-none">
            Or
          </span>
          <Separator className="tw-flex-1 tw-h-0.5 tw-bg-gray-600" />
        </div>

        <div className="tw-flex tw-items-center tw-gap-4 tw-pb-8">
          <Button
            type="button"
            variant="outline"
            className="tw-w-full tw-h-12 tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-text-white tw-text-2xl"
          >
            Log In
          </Button>
          <Button
            type="button"
            variant="outline"
            className="tw-w-full tw-h-12 tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-text-white tw-text-2xl"
          >
            Sign Up
          </Button>
        </div>
      </div>
    </div>
  )
}
